import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Modal,
  Fab,
  Chip,
  makeStyles,
} from '@material-ui/core';
import BillingDate from '../../components/invoice/BillingDate';
import { Invoice, InvoiceModel } from '../../models/InvoiceModel';

import {
  currentYMD,
  dailyReportYMD,
  dateToYMD,
  displayToYearMonthDay,
  ymdToDate,
} from '../../util/DateUtil';
import DailyReportSelectMember from '../../components/dailyReport/DailyReportSelectMember';
import { DailyReport, DailyReportModel } from '../../models/DailyReport/DailyReportModel';
import ConfirmModal from '../../components/modal/ConfirmModal';
import DailyReportSelectInvoiceTable from '../../components/dailyReport/DailyReportSelectInvoiceTable';
import NotificationToast from '../../components/common/NotificationToast';
import { DailyReportMemberModel } from '../../models/DailyReport/DailyReportMemberModel';

import { SCREENS } from '../../constants/Screens';
import { formatLocalNumber } from '../../util/Math';
import DailyReportInvoiceTable from '../../components/dailyReport/DailyReportInvoiceTable';

const useStyles = makeStyles(theme => ({
  chipRecorded: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
    fontWeight: 'bold',
  },
  chipUnreported: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    fontWeight: 'bold',
  },
}));

/**
 * Context用のインターフェース
 * 日報・請求書
 */
interface DailyReportInvoicesContextProps {
  dailyReportInvoices: Invoice[];
  setDailyReportInvoices: (e: Invoice[]) => void;
}
export const DailyReportInvoicesContext = createContext({} as DailyReportInvoicesContextProps);
/**
 * Context用のインターフェース
 * 日報・本体
 */
interface DailyReportContextProps {
  dailyReport: DailyReport | null;
  setDailyReportInvoices: (e: DailyReport | null) => void;
}
export const DailyReportContext = createContext({} as DailyReportContextProps);

/**
 * 日報・請求書Context
 */

const DailyReportCreate = (props: any) => {
  const { navigation } = props;

  const [dailyReports, setDailyReports] = useState<DailyReport | null>(null);
  const [isMemberModalOpen, setMemberModalOpen] = useState(false);
  const [isRequestModalOpen, setRequestModalOpen] = useState(false);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false);
  const [ymd, setYmd] = useState(dailyReportYMD());
  const [memo, setMemo] = useState<string>('');
  const [dailyReportInvoices, setDailyReportInvoices] = useState<Invoice[]>([]);

  /**モーダルの表示・非表示 */
  const openMemberModal = () => setMemberModalOpen(true);
  const closeMemberModal = () => setMemberModalOpen(false);
  const openRequestModal = () => setRequestModalOpen(true);
  const closeRequestModal = () => setRequestModalOpen(false);
  const openConfirmModal = () => setConfirmModalOpen(true);
  const closeConfirmModal = () => setConfirmModalOpen(false);

  const modalStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxHeight: '80vh', // 最大の高さを指定してスクロール可能にする
    backgroundColor: 'white',
    padding: 16, // MUI v4では数値を直接指定
    borderRadius: 8,
    overflowY: 'auto', // 縦方向にスクロールを許可
  };

  const modalInvoiceStyle: React.CSSProperties = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '80%',
    maxHeight: '80vh', // 最大の高さを指定してスクロール可能にする
    backgroundColor: 'white',
    padding: 16, // MUI v4では数値を直接指定
    borderRadius: 8,
    overflowY: 'auto', // 縦方向にスクロールを許可
  };

  /**
   * 登録処理
   */
  const onRegister = async () => {
    try {
      const dailyReportModel = new DailyReportModel({
        id: dailyReports?.id || 0,
        created_at: null,
        updated_at: null,
        deleted_at: null,
        user_id: dailyReports?.user_id || 0,
        company_id: dailyReports?.company_id || 0,
        ymd: ymd,
        memo: memo ?? '',
        invoices: dailyReportInvoices,
        user: null,
        comments: [], // 必須の comments プロパティを初期化
      });

      await dailyReportModel.save();
      closeConfirmModal();
      NotificationToast().saved();
      fetchByYmd();

      navigation.navigate(SCREENS.DailyReport.List);
    } catch (error) {
      closeConfirmModal();
      NotificationToast().error('登録に失敗しました。');
    }
  };

  /**
   * Calendarの日付を変更したときのコールバック
   * @param {Date} date 選択された日付
   */
  const onChangeDate = (date: Date) => {
    setYmd(dateToYMD(date));
  };

  /**
   * 指定日の日報を取得し、Stateを更新する
   * もし日報が存在しない場合は、指定日の請求を取得し、Stateを更新する
   * @returns {Promise<void>}
   */
  const fetchByYmd = async () => {
    const dailyReport = await DailyReportModel.fetchByYmd({ ymd: ymd });
    setDailyReports(dailyReport);
    setMemo(dailyReport ? dailyReport.memo : '');

    // 日報が存在する場合
    if (dailyReport?.id !== undefined) {
      setDailyReportInvoices(dailyReport.invoices ? dailyReport.invoices : []);
      return;
    }
    // 日報が存在しない場合
    // 請求日の請求書を取得
    const result = await InvoiceModel.fetchMyInvoiceListByYmd({ ymd: ymd });
    setDailyReportInvoices(result);
  };

  /**
   * 指定のメンバーを日報メンバーに登録する
   * @param {number[]} member_ids メンバーのID
   * @returns {Promise<void>}
   */
  const saveMembers = async (member_ids: number[]) => {
    closeMemberModal();
    try {
      await DailyReportMemberModel.save({ other_user_ids: member_ids });
      NotificationToast().saved();
    } catch (error) {
      NotificationToast().error('登録に失敗しました。');
    }
  };

  const StatusTag = () => {
    const classes = useStyles();

    return dailyReports?.id !== undefined ? (
      <Chip label="報告済み" className={classes.chipRecorded} size="small" />
    ) : (
      <Chip label="未報告" className={classes.chipUnreported} size="small" />
    );
  };

  const sumTotalAmount = () => {
    const totalAmount = dailyReportInvoices.reduce((total, invoice) => {
      return total + invoice.total;
    }, 0);
    return formatLocalNumber(totalAmount);
  };

  /*****************************
   * useEffect
   * ***************************
   */

  useEffect(() => {
    fetchByYmd();
  }, [ymd]);

  useEffect(() => {
    fetchByYmd();
  }, []);

  console.log('DailyReportDetailScreen', dailyReports);
  return (
    <Box
      sx={{
        overflow: 'auto',
        height: '100vh',
        bgcolor: '#e0f7da',
        p: 3,
      }}
    >
      {/* 日付 */}
      <Box component={Paper} p={1}>
        <Box display="flex" justifyContent="center">
          {StatusTag()}
        </Box>
        <BillingDate
          title="日付"
          billingDate={displayToYearMonthDay(ymd)}
          dt={ymdToDate(ymd)}
          onSelectCalendar={onChangeDate}
        />
      </Box>
      {/* メンバー */}
      {/* <Box mt={3} display="flex" justifyContent="center">
        <Button variant="contained" color="primary" onClick={openMemberModal}>
          報告するメンバーを選択
        </Button>
      </Box> */}

      {/* 請求書の選択 */}
      <DailyReportInvoicesContext.Provider
        value={{
          dailyReportInvoices: dailyReportInvoices,
          setDailyReportInvoices: setDailyReportInvoices,
        }}
      >
        <DailyReportInvoiceTable ymd={ymd} dailyReport={dailyReports} />
      </DailyReportInvoicesContext.Provider>
      <Box display="flex" justifyContent="center" mt={2}>
        <Button variant="contained" color="primary" onClick={openRequestModal}>
          他の請求を追加
        </Button>
      </Box>

      <Box mt={3} component={Paper} p={1}>
        <Typography
          variant="h5"
          display="block"
          gutterBottom
          color="error"
          style={{ fontWeight: 'bold' }}
        >
          {`本日の成果：${sumTotalAmount()}円`}
        </Typography>
        <Typography variant="h6">所感</Typography>
        <TextField
          // label="所感"
          multiline
          minRows={4}
          variant="outlined"
          fullWidth
          value={memo}
          onChange={e => setMemo(e.target.value)}
        />
      </Box>

      <Box mt={3}>
        <Button onClick={openConfirmModal} variant="contained" color="primary" fullWidth>
          投稿
        </Button>
      </Box>
      {/* 確認モーダル */}
      <ConfirmModal
        onCancel={closeConfirmModal}
        onConfirm={onRegister}
        isOpen={isConfirmModalOpen}
      />

      {/* メンバー選択モーダル */}
      <Modal open={isMemberModalOpen} onClose={closeMemberModal}>
        <Paper style={modalStyle}>
          <DailyReportSelectMember onSave={saveMembers} />
        </Paper>
      </Modal>

      {/* 他の請求を追加モーダル */}
      <Modal open={isRequestModalOpen} onClose={closeRequestModal}>
        <Paper style={modalInvoiceStyle}>
          {/* <Typography variant="h6" gutterBottom>
            他の請求を追加
          </Typography> */}
          {/* 請求書の選択 */}
          <DailyReportInvoicesContext.Provider
            value={{
              dailyReportInvoices: dailyReportInvoices,
              setDailyReportInvoices: setDailyReportInvoices,
            }}
          >
            <DailyReportSelectInvoiceTable dailyReport={dailyReports} />
          </DailyReportInvoicesContext.Provider>

          {/* 請求を追加するためのフォーム */}
          <Button onClick={closeRequestModal} variant="contained" color="primary">
            閉じる
          </Button>
        </Paper>
      </Modal>
      {/* フローティングボタン */}
      <Fab
        color="primary"
        aria-label="add"
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
        }}
        onClick={openConfirmModal}
      >
        <Typography variant="body1">投稿</Typography>
        {/* <AddIcon /> */}
      </Fab>
    </Box>
  );
};

export default DailyReportCreate;
